import { uiText, useUiLanguage, statusText } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, Send, Calendar } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { formatDateToDDMMYYYY } from '@/lib/utils';

interface SupportRequest {
  id: string;
  subject: string;
  message: string;
  priority: string;
  status: string;
  created_at: string;
  response: string | null;
  resolved_at: string | null;
  admin_id: string;
  dojo_id: string;
  profiles: {
    first_name: string;
    last_name: string;
    email: string;
  };
  dojos: {
    name: string;
  };
}

const SupportRequestsTab: React.FC = () => {
  useUiLanguage();
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<SupportRequest | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [newStatus, setNewStatus] = useState<string>('');

  useEffect(() => {
    fetchSupportRequests();
  }, []);

  const fetchSupportRequests = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('support_requests')
        .select(`
          *,
          profiles!support_requests_admin_id_fkey (first_name, last_name, email),
          dojos!support_requests_dojo_id_fkey (name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Error fetching support requests:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to fetch support requests"),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!selectedRequest || !replyMessage.trim()) return;

    try {
      // Update the support request with reply and status
      const { error: updateError } = await supabase
        .from('support_requests')
        .update({
          response: replyMessage,
          status: newStatus || 'replied',
          resolved_at: newStatus === 'resolved' ? new Date().toISOString() : null
        })
        .eq('id', selectedRequest.id);

      if (updateError) throw updateError;

      // Send reply email via edge function
      const { error: emailError } = await supabase.functions.invoke('send-support-reply', {
        body: {
          to_email: selectedRequest.profiles.email,
          admin_name: `${selectedRequest.profiles.first_name} ${selectedRequest.profiles.last_name}`,
          original_subject: selectedRequest.subject,
          reply_message: replyMessage,
          dojo_name: selectedRequest.dojos.name
        }
      });

      if (emailError) {
        console.error('Error sending reply email:', emailError);
        // Don't throw here - the reply was saved, just email failed
      }

      toast({
        title: uiText("Reply Sent"),
        description: uiText("Your reply has been sent successfully"),
      });

      setReplyDialogOpen(false);
      setReplyMessage('');
      setNewStatus('');
      setSelectedRequest(null);
      fetchSupportRequests();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to send reply"),
        variant: 'destructive'
      });
    }
  };

  const openReplyDialog = (request: SupportRequest) => {
    setSelectedRequest(request);
    setNewStatus(request.status);
    setReplyDialogOpen(true);
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'open': return 'default';
      case 'replied': return 'secondary';
      case 'resolved': return 'outline';
      default: return 'outline';
    }
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'destructive';
      case 'high': return 'default';
      case 'normal': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            {uiText("Support Requests")}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">{uiText("Loading support requests...")}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{uiText("Subject")}</TableHead>
                  <TableHead>{uiText("From")}</TableHead>
                  <TableHead>{uiText("Dojo")}</TableHead>
                  <TableHead>{uiText("Priority")}</TableHead>
                  <TableHead>{uiText("Status")}</TableHead>
                  <TableHead>{uiText("Created")}</TableHead>
                  <TableHead>{uiText("Actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell className="font-medium max-w-xs truncate">
                      {request.subject}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {request.profiles.first_name} {request.profiles.last_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {request.profiles.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{request.dojos.name}</TableCell>
                    <TableCell>
                      <Badge variant={getPriorityBadgeVariant(request.priority)}>
                        {request.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(request.status)}>
                        {statusText(request.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="h-3 w-3" />
                        {formatDateToDDMMYYYY(request.created_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openReplyDialog(request)}
                      >
                        <Send className="h-3 w-3 mr-1" />
                        {uiText("Reply")}</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!loading && requests.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              {uiText("No support requests found")}</div>
          )}
        </CardContent>
      </Card>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{uiText("Reply to Support Request")}</DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              {/* Original Request */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">{selectedRequest.subject}</h4>
                    <Badge variant={getPriorityBadgeVariant(selectedRequest.priority)}>
                      {selectedRequest.priority}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">
                    {uiText("From:")}{' '}{selectedRequest.profiles.first_name} {selectedRequest.profiles.last_name} ({selectedRequest.profiles.email})
                  </p>
                  <p className="whitespace-pre-wrap">{selectedRequest.message}</p>
                </CardContent>
              </Card>

              {/* Reply Form */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="reply">{uiText("Your Reply")}</Label>
                  <Textarea
                    id="reply"
                    placeholder={uiText("Type your reply here...")}
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={6}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="status">{uiText("Update Status")}</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder={uiText("Select status")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">{uiText("Open")}</SelectItem>
                      <SelectItem value="replied">{uiText("Replied")}</SelectItem>
                      <SelectItem value="resolved">{uiText("Resolved")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setReplyDialogOpen(false)}>
                    {uiText("Cancel")}</Button>
                  <Button onClick={handleReply} disabled={!replyMessage.trim()}>
                    {uiText("Send Reply")}</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportRequestsTab;
